import { supabase } from '../lib/supabase';

export interface InstructorProfile {
  id: string;
  tenant_id: string;
  user_id: string | null;
  name: string;
  email: string;
  bio: string | null;
  avatar_url: string | null;
  expertise: string[] | null;
  social_links: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  phone?: string | null;
  website?: string | null;
  location?: string | null;
}

export interface InstructorPermission {
  id: string;
  instructor_id: string;
  permission_type: string;
  is_granted: boolean;
  granted_by: string | null;
  granted_at: string | null;
  tenant_id: string;
  created_at: string;
  updated_at: string;
}

export interface LiveSession {
  id: string;
  course_id: string;
  instructor_id: string;
  title: string;
  description: string | null;
  scheduled_at: string;
  duration_minutes: number;
  max_participants: number | null;
  meeting_url: string | null;
  meeting_id: string | null;
  meeting_password: string | null;
  status: 'scheduled' | 'live' | 'completed' | 'cancelled';
  recording_url: string | null;
  attendance_count: number;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  course?: {
    title: string;
    slug: string;
  };
}

export interface InstructorAssessment {
  id: string;
  instructor_id: string;
  course_id: string;
  title: string;
  description: string | null;
  assessment_type: 'quiz' | 'assignment' | 'project' | 'exam';
  instructions: string | null;
  duration_minutes: number | null;
  max_attempts: number;
  passing_score: number | null;
  is_published: boolean;
  due_date: string | null;
  available_from: string | null;
  available_until: string | null;
  questions: any;
  grading_rubric: any | null;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  course?: {
    title: string;
    slug: string;
  };
}

// Service pour la gestion du profil instructeur
export class InstructorService {
  
  /**
   * Récupère le profil complet d'un instructeur
   */
  static async getInstructorProfile(tenantId: string, instructorId: string): Promise<InstructorProfile | null> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructors')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('id', instructorId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération du profil instructeur:', error);
      throw error;
    }
  }

  /**
   * Met à jour le profil d'un instructeur
   */
  static async updateInstructorProfile(
    tenantId: string, 
    instructorId: string, 
    updates: Partial<InstructorProfile>
  ): Promise<InstructorProfile> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructors')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', tenantId)
        .eq('id', instructorId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  }

  /**
   * Récupère les permissions d'un instructeur
   */
  static async getInstructorPermissions(tenantId: string, instructorId: string): Promise<InstructorPermission[]> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructor_permissions')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des permissions:', error);
      throw error;
    }
  }

  /**
   * Vérifie si un instructeur a une permission spécifique
   */
  static async hasPermission(
    tenantId: string, 
    instructorId: string, 
    permissionType: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructor_permissions')
        .select('is_granted')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId)
        .eq('permission_type', permissionType)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data?.is_granted || false;
    } catch (error) {
      console.error('Erreur lors de la vérification des permissions:', error);
      return false;
    }
  }

  /**
   * Accorde ou révoque une permission à un instructeur
   */
  static async setPermission(
    tenantId: string,
    instructorId: string,
    permissionType: string,
    isGranted: boolean,
    grantedBy?: string
  ): Promise<InstructorPermission> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructor_permissions')
        .upsert({
          instructor_id: instructorId,
          permission_type: permissionType,
          is_granted: isGranted,
          granted_by: grantedBy || null,
          granted_at: isGranted ? new Date().toISOString() : null,
          tenant_id: tenantId
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des permissions:', error);
      throw error;
    }
  }

  /**
   * Récupère les sessions live d'un instructeur
   */
  static async getInstructorLiveSessions(
    tenantId: string, 
    instructorId: string,
    options: {
      status?: 'scheduled' | 'live' | 'completed' | 'cancelled';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<LiveSession[]> {
    try {
      let query = supabase
        .from('elearn_live_sessions')
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId)
        .order('scheduled_at', { ascending: false });

      if (options.status) {
        query = query.eq('status', options.status);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des sessions live:', error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle session live
   */
  static async createLiveSession(
    tenantId: string,
    sessionData: Omit<LiveSession, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'attendance_count'>
  ): Promise<LiveSession> {
    try {
      const { data, error } = await supabase
        .from('elearn_live_sessions')
        .insert({
          ...sessionData,
          tenant_id: tenantId,
          attendance_count: 0
        })
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la création de la session live:', error);
      throw error;
    }
  }

  /**
   * Met à jour une session live
   */
  static async updateLiveSession(
    tenantId: string,
    sessionId: string,
    updates: Partial<LiveSession>
  ): Promise<LiveSession> {
    try {
      const { data, error } = await supabase
        .from('elearn_live_sessions')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', tenantId)
        .eq('id', sessionId)
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la session live:', error);
      throw error;
    }
  }

  /**
   * Supprime une session live
   */
  static async deleteLiveSession(tenantId: string, sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('elearn_live_sessions')
        .delete()
        .eq('tenant_id', tenantId)
        .eq('id', sessionId);

      if (error) throw error;
    } catch (error) {
      console.error('Erreur lors de la suppression de la session live:', error);
      throw error;
    }
  }

  /**
   * Récupère les évaluations créées par un instructeur
   */
  static async getInstructorAssessments(
    tenantId: string,
    instructorId: string,
    options: {
      courseId?: string;
      isPublished?: boolean;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<InstructorAssessment[]> {
    try {
      let query = supabase
        .from('elearn_instructor_assessments')
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId)
        .order('created_at', { ascending: false });

      if (options.courseId) {
        query = query.eq('course_id', options.courseId);
      }

      if (options.isPublished !== undefined) {
        query = query.eq('is_published', options.isPublished);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des évaluations:', error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle évaluation
   */
  static async createAssessment(
    tenantId: string,
    assessmentData: Omit<InstructorAssessment, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>
  ): Promise<InstructorAssessment> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructor_assessments')
        .insert({
          ...assessmentData,
          tenant_id: tenantId
        })
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'évaluation:', error);
      throw error;
    }
  }

  /**
   * Met à jour une évaluation
   */
  static async updateAssessment(
    tenantId: string,
    assessmentId: string,
    updates: Partial<InstructorAssessment>
  ): Promise<InstructorAssessment> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructor_assessments')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', tenantId)
        .eq('id', assessmentId)
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'évaluation:', error);
      throw error;
    }
  }

  /**
   * Supprime une évaluation
   */
  static async deleteAssessment(tenantId: string, assessmentId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('elearn_instructor_assessments')
        .delete()
        .eq('tenant_id', tenantId)
        .eq('id', assessmentId);

      if (error) throw error;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'évaluation:', error);
      throw error;
    }
  }

  /**
   * Publie ou dépublie une évaluation
   */
  static async toggleAssessmentPublication(
    tenantId: string,
    assessmentId: string,
    isPublished: boolean
  ): Promise<InstructorAssessment> {
    try {
      const { data, error } = await supabase
        .from('elearn_instructor_assessments')
        .update({
          is_published: isPublished,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', tenantId)
        .eq('id', assessmentId)
        .select(`
          *,
          course:course_id(title, slug)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur lors de la publication/dépublication:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques générales d'un instructeur
   */
  static async getInstructorStats(tenantId: string, instructorId: string) {
    try {
      // Récupération des cours
      const { data: courses, error: coursesError } = await supabase
        .from('elearn_courses')
        .select('id, title, status')
        .eq('tenant_id', tenantId)
        .eq('instructor_id', instructorId);

      if (coursesError) throw coursesError;

      const courseIds = courses?.map(c => c.id) || [];

      // Récupération des inscriptions
      const { data: enrollments, error: enrollmentsError } = await supabase
        .from('elearn_enrollments')
        .select('id, student_id, course_id, status, progress')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds);

      if (enrollmentsError) throw enrollmentsError;

      // Récupération des avis
      const { data: reviews, error: reviewsError } = await supabase
        .from('elearn_course_reviews')
        .select('rating, review_text')
        .eq('tenant_id', tenantId)
        .in('course_id', courseIds);

      if (reviewsError) throw reviewsError;

      // Calcul des statistiques
      const totalCourses = courses?.length || 0;
      const publishedCourses = courses?.filter(c => c.status === 'published').length || 0;
      const totalStudents = new Set(enrollments?.map(e => e.student_id)).size;
      const totalEnrollments = enrollments?.length || 0;
      const completedEnrollments = enrollments?.filter(e => e.status === 'completed').length || 0;
      const averageProgress = enrollments?.length > 0 
        ? enrollments.reduce((sum, e) => sum + (e.progress || 0), 0) / enrollments.length 
        : 0;
      
      const totalReviews = reviews?.length || 0;
      const averageRating = totalReviews > 0 
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews 
        : 0;

      return {
        totalCourses,
        publishedCourses,
        draftCourses: totalCourses - publishedCourses,
        totalStudents,
        totalEnrollments,
        completedEnrollments,
        completionRate: totalEnrollments > 0 ? (completedEnrollments / totalEnrollments) * 100 : 0,
        averageProgress: Math.round(averageProgress),
        totalReviews,
        averageRating: Math.round(averageRating * 10) / 10
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }
}

export default InstructorService;
